import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { getOutboundCallBaseUrl } from '../lib/apiConfig';

const Connections = () => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', accessToken: '', refreshToken: '', email: '' });

  // Load connections
  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const apiBase = getOutboundCallBaseUrl(); // v2 server (3017) — where /api/connections lives

      const res = await fetch(`${apiBase}/api/connections`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        setConnections(data.connections);
      }
    } catch (error) {
      console.error('Failed to load connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddConnection = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Please enter a connection name');
      return;
    }

    if (!formData.accessToken.trim()) {
      alert('Please enter an access token');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiBase = getOutboundCallBaseUrl(); // v2 server (3017) — where /api/connections lives

      const res = await fetch(`${apiBase}/api/connections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'google_sheets',
          name: formData.name,
          credentials: {
            accessToken: formData.accessToken,
            refreshToken: formData.refreshToken || null,
            email: formData.email || null,
          },
        }),
      });

      const data = await res.json();
      if (data.success) {
        alert('✓ Google Sheets connection added!');
        setFormData({ name: '', accessToken: '', refreshToken: '', email: '' });
        setShowAddForm(false);
        loadConnections();
      } else {
        alert(`✗ Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Failed to add connection:', error);
      alert('Failed to add connection');
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    if (!window.confirm('Delete this connection?')) return;

    try {
      const token = localStorage.getItem('token');
      const apiBase = getOutboundCallBaseUrl(); // v2 server (3017) — where /api/connections lives

      const res = await fetch(`${apiBase}/api/connections/${connectionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        alert('✓ Connection deleted');
        loadConnections();
      }
    } catch (error) {
      console.error('Failed to delete connection:', error);
      alert('Failed to delete connection');
    }
  };

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">📊 Google Sheets Connections</h1>
        <p className="text-gray-600">
          Connect your Google Sheets account. You can then use these connections in your scripts.
        </p>
      </div>

      {/* Add Connection Button */}
      <div className="mb-6">
        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            <Plus size={20} />
            Add Google Sheets Connection
          </button>
        ) : (
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h2 className="text-xl font-semibold mb-4">Add Google Sheets Connection</h2>

            {/* Connection Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Connection Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., My Personal Google Account"
                className="w-full border rounded px-3 py-2"
              />
            </div>

            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Email (optional)</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@gmail.com"
                className="w-full border rounded px-3 py-2"
              />
            </div>

            {/* Access Token */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Access Token *</label>
              <textarea
                value={formData.accessToken}
                onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
                placeholder="Paste your Google OAuth access token here"
                className="w-full border rounded px-3 py-2 font-mono text-sm min-h-24"
              />
              <p className="text-xs text-gray-500 mt-1">Required</p>
            </div>

            {/* Refresh Token */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Refresh Token (optional)</label>
              <textarea
                value={formData.refreshToken}
                onChange={(e) => setFormData({ ...formData, refreshToken: e.target.value })}
                placeholder="Paste your refresh token for long-term access"
                className="w-full border rounded px-3 py-2 font-mono text-sm min-h-20"
              />
              <p className="text-xs text-gray-500 mt-1">Optional - for automatic token refresh</p>
            </div>

            {/* Help Section */}
            <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
              <h3 className="font-medium text-sm mb-2">How to get your Google OAuth tokens:</h3>
              <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a></li>
                <li>Create a new project (or use existing)</li>
                <li>Enable <strong>Google Sheets API</strong></li>
                <li>Go to <strong>Credentials</strong> → <strong>Create Credentials</strong> → <strong>OAuth 2.0 Client ID</strong></li>
                <li>Choose <strong>Web application</strong></li>
                <li>Add authorized redirect URI: <code className="bg-white px-2 py-1 rounded text-xs">http://localhost:3000/auth/callback</code></li>
                <li>Download JSON and use the tokens</li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleAddConnection}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Save Connection
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Connections List */}
      {loading ? (
        <p className="text-gray-500">Loading connections...</p>
      ) : connections.length === 0 ? (
        <p className="text-gray-500 text-center py-8">
          No Google Sheets connections yet. <br />
          <button
            onClick={() => setShowAddForm(true)}
            className="text-blue-600 hover:underline mt-2"
          >
            Add one now
          </button>
        </p>
      ) : (
        <div className="grid gap-4">
          {connections.map((conn) => (
            <div key={conn._id} className="bg-white border rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                <span className="text-3xl">📊</span>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{conn.name}</h3>
                    <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-medium">
                      ✓ Connected
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {conn.email ? `${conn.email}` : 'Google Sheets'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {conn.lastUsed
                      ? `Last used: ${new Date(conn.lastUsed).toLocaleDateString()}`
                      : 'Never used'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDeleteConnection(conn._id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                title="Delete connection"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Connections;
